import React, { useEffect } from 'react';
import { Routes } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CssBaseline } from '@mui/material';
import theme from './theme/theme';
import { useAuthStore } from './stores/authStore';
import { getAppointmentTypes } from './services/appointmentService';
import { getSchedules } from './services/scheduleService';
import PublicRoutes from './routes/PublicRoutes';
import ProtectedRoutes from './routes/ProtectedRoutes';
import CatchAllRoute from './routes/CatchAllRoute';
import LoadingSpinner from './components/common/LoadingSpinner';
import './styles/MuiCustomStyles.css';

const App: React.FC = () => {
  const {
    isAuthenticated,
    checkSessionExpiration,
    registerActivity,
    hasHydrated,
    user,
    requiresPasswordChange,
    setInitialSetupStatus,
  } = useAuthStore();

  // Marca el store como hidratado usando la API pública de zustand persist.
  // Referenciar useAuthStore dentro de onRehydrateStorage es inseguro con storage
  // síncrona (TDZ): el error queda swalleado y hasHydrated nunca flipea.
  useEffect(() => {
    if (useAuthStore.persist.hasHydrated()) {
      useAuthStore.setState({ hasHydrated: true });
    }
    return useAuthStore.persist.onFinishHydration(() => {
      useAuthStore.setState({ hasHydrated: true });
    });
  }, []);

  // Verificar expiración de sesión al cargar la aplicación
  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    checkSessionExpiration();
  }, [hasHydrated, checkSessionExpiration]);

  // Reiniciar timeout de sesión cuando hay actividad del usuario
  useEffect(() => {
    if (!hasHydrated || !isAuthenticated) {
      return;
    }

    const handleUserActivity = () => {
      registerActivity();
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    activityEvents.forEach(eventName => {
      window.addEventListener(eventName, handleUserActivity);
    });

    const expirationInterval = setInterval(() => {
      checkSessionExpiration();
    }, 30_000);

    return () => {
      activityEvents.forEach(eventName => {
        window.removeEventListener(eventName, handleUserActivity);
      });
      clearInterval(expirationInterval);
    };
  }, [hasHydrated, isAuthenticated, registerActivity, checkSessionExpiration]);

  useEffect(() => {
    if (!hasHydrated || !isAuthenticated) {
      return;
    }

    if (requiresPasswordChange) {
      setInitialSetupStatus(false);
      return;
    }

    const institutionId = user?.institution?.id;
    const specialtyId = user?.specialty?.id;

    if (!institutionId || !specialtyId) {
      setInitialSetupStatus(false);
      return;
    }

    let cancelled = false;

    const checkInitialSetup = async () => {
      const [appointmentTypesResponse, schedulesResponse] = await Promise.all([
        getAppointmentTypes(institutionId, specialtyId),
        getSchedules(institutionId),
      ]);

      if (cancelled) {
        return;
      }

      if (!appointmentTypesResponse.success || !appointmentTypesResponse.data) {
        setInitialSetupStatus(true);
        return;
      }

      const hasActiveType = appointmentTypesResponse.data.some(type => type.isActive);

      const hasActiveSchedule =
        schedulesResponse.success &&
        !!schedulesResponse.data &&
        schedulesResponse.data.some(
          schedule =>
            schedule.userId === user?.id &&
            schedule.specialtyId === specialtyId &&
            schedule.isActive
        );

      setInitialSetupStatus(!(hasActiveType && hasActiveSchedule));
    };

    void checkInitialSetup();

    return () => {
      cancelled = true;
    };
  }, [
    hasHydrated,
    isAuthenticated,
    user?.institution?.id,
    user?.specialty?.id,
    requiresPasswordChange,
    setInitialSetupStatus,
  ]);

  if (!hasHydrated) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh' }}>
          <LoadingSpinner />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Routes>
        {PublicRoutes({ isAuthenticated })}
        {ProtectedRoutes({})}
        {CatchAllRoute({ isAuthenticated })}
      </Routes>
    </ThemeProvider>
  );
};

export default App;
